'use client'

import * as React from 'react'
import { CalendarIcon } from '@radix-ui/react-icons'
import { format, getHours, getMinutes, setHours, setMinutes } from 'date-fns'

import { cn } from '@/common/lib/utils'
import { Calendar } from '@/common/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/common/ui/popover'
import { ScrollArea, ScrollBar } from '@/common/ui/scroll-area'
import { Button, ButtonProps } from '@/common/ui/button'

export interface DateTimePickerProps extends ButtonProps {
	onDateSelect: (date?: Date) => void
	date?: Date
	placeholder?: string
}

export function DateTimePicker({
	onDateSelect,
	date,
	placeholder,
	className,
	...props
}: DateTimePickerProps) {
	const [isOpen, setIsOpen] = React.useState(false)

	const hours = Array.from({ length: 24 }, (_, i) => `${i}`.padStart(2, '0'))
	const minutes = Array.from({ length: 60 }, (_, i) => `${i}`.padStart(2, '0'))

	const handleTimeChange = (type: 'hour' | 'minute', value: string) => {
		if (date) {
			const newDate = new Date(date) // Create a new Date object from the existing date
			const updatedDate =
				type === 'hour'
					? setHours(newDate, parseInt(value))
					: setMinutes(newDate, parseInt(value))
			onDateSelect(updatedDate)
		}
	}

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					className={cn(
						'w-full justify-start text-left font-normal',
						!date && 'text-muted-foreground',
						className,
					)}
					{...props}
				>
					<CalendarIcon className="mr-2 h-4 w-4" />
					{date ? (
						format(date, 'MM/dd/yyyy HH:mm')
					) : (
						<span>{placeholder || 'เลือกวันและเวลา'}</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0">
				<div className="sm:flex">
					<Calendar
						mode="single"
						selected={date}
						onSelect={onDateSelect}
						initialFocus
					/>
					<div className="flex flex-col divide-y sm:h-[300px] sm:flex-row sm:divide-x sm:divide-y-0">
						<ScrollArea className="w-64 sm:w-auto">
							<div className="flex p-2 sm:flex-col">
								{hours.map((hour) => (
									<Button
										key={hour}
										size="icon"
										variant={
											date &&
											getHours(date).toString().padStart(2, '0') === hour
												? 'default'
												: 'ghost'
										}
										className="aspect-square shrink-0 sm:w-full"
										onClick={() => handleTimeChange('hour', hour.toString())}
									>
										{hour}
									</Button>
								))}
							</div>
							<ScrollBar orientation="horizontal" className="sm:hidden" />
						</ScrollArea>
						<ScrollArea className="w-64 sm:w-auto">
							<div className="flex p-2 sm:flex-col">
								{minutes.map((minute) => (
									<Button
										key={minute}
										size="icon"
										variant={
											date &&
											getMinutes(date).toString().padStart(2, '0') === minute
												? 'default'
												: 'ghost'
										}
										className="aspect-square shrink-0 sm:w-full"
										onClick={() =>
											handleTimeChange('minute', minute.toString())
										}
									>
										{minute.toString().padStart(2, '0')}
									</Button>
								))}
							</div>
							<ScrollBar orientation="horizontal" className="sm:hidden" />
						</ScrollArea>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}
